import type { IGraphClient } from '../../src/services/GraphService';
import type { IColumnInfo, ILibraryMetadata, ITagColumn } from '../../src/tagging/Tagging';

interface IGraphColumn {
  id: string;
  name: string;
  displayName: string;
  readOnly?: boolean;
  text?: object;
  number?: object;
  choice?: { choices?: string[]; allowTextEntry?: boolean };
}

/**
 * Library columns and item values through Microsoft Graph. Reading uses Sites.Read.All;
 * creating columns and writing values use Sites.Manage.All (requested on first use).
 */
export class GraphLibraryMetadata implements ILibraryMetadata {
  private _listId: Promise<string> | undefined;

  constructor(
    private readonly _read: IGraphClient,
    private readonly _manage: IGraphClient,
    private readonly _siteId: string,
    private readonly _driveId: string
  ) {}

  public async getColumns(): Promise<IColumnInfo[]> {
    const page: { value: IGraphColumn[] } = await this._read
      .api(`/sites/${this._siteId}/lists/${await this._list()}/columns?$select=id,name,displayName,readOnly,text,number,choice`).get();
    return page.value.map(c => ({
      id: c.id,
      name: c.name,
      displayName: c.displayName,
      readOnly: !!c.readOnly,
      type: c.choice ? 'choice' : c.number ? 'number' : c.text ? 'text' : 'other',
      choices: c.choice?.choices,
      allowTextEntry: c.choice?.allowTextEntry
    }));
  }

  public async createColumn(column: ITagColumn, choices?: string[]): Promise<void> {
    const definition: object = column.type === 'choice'
      ? { choice: { choices: choices ?? [], displayAs: 'dropDownMenu', allowTextEntry: false } }
      : column.type === 'number' ? { number: { decimalPlaces: 'none' } } : { text: {} };
    await this._manage.api(`/sites/${this._siteId}/lists/${await this._list()}/columns`)
      .post({ name: column.name, displayName: column.displayName, ...definition });
  }

  public async setChoices(column: IColumnInfo, choices: string[]): Promise<void> {
    await this._manage.api(`/sites/${this._siteId}/lists/${await this._list()}/columns/${column.id}`)
      .patch({ choice: { choices } });
  }

  public async getFields(itemId: string): Promise<{ [column: string]: unknown }> {
    return this._read.api(`/drives/${this._driveId}/items/${itemId}/listItem/fields`).get();
  }

  public async setFields(itemId: string, fields: { [column: string]: unknown }): Promise<void> {
    await this._manage.api(`/drives/${this._driveId}/items/${itemId}/listItem/fields`).patch(fields);
  }

  private _list(): Promise<string> {
    if (!this._listId) {
      this._listId = this._read.api(`/drives/${this._driveId}/list?$select=id`).get().then((l: { id: string }) => l.id);
    }
    return this._listId;
  }
}
